import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ArrowBack,
  Edit,
  Home,
  Bed,
  Bathtub,
  SquareFoot,
  AttachMoney,
  PhotoCamera,
  Videocam,
  Delete,
  CheckCircle,
  Star,
  CloudUpload,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { unitApi, Unit, unitTypeApi, UnitType, dailyRatesApi } from '../services/api';
import { getStorageUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import EditUnitDialog from '../components/EditUnitDialog';

interface UnitFormData {
  unit_number: string;
  type: string; // Dynamic unit type
  name: string;
  rent_amount: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  status: 'vacant' | 'occupied' | 'maintenance';
  description?: string;
  rental_types: ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[];
  living_room_count?: number;
  mens_guest_room_count?: number;
  womens_guest_room_count?: number;
  single_bed_count?: number;
  double_bed_count?: number;
  max_persons?: number;
  checkin_time?: string;
  checkout_time?: string;
  tax_rate?: number;
}

const UnitDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [updateAllRates, setUpdateAllRates] = useState(false);

  // Fetch unit details
  const { data: unit, isLoading, error } = useQuery({
    queryKey: ['unit', id],
    queryFn: () => unitApi.getById(Number(id)),
    enabled: !!id,
  });

  // Fetch unit types for unit type selection
  const { data: unitTypes = [], isLoading: unitTypesLoading } = useQuery({
    queryKey: ['unitTypes'],
    queryFn: async () => {
      return await unitTypeApi.getActive();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Function to build parent hierarchy string
  const buildParentHierarchy = (unit: Unit, propertyName?: string): string => {
    if (!unit.parent) {
      // This is a root unit - include property name if available
      if (propertyName) {
        return `${propertyName} -> ${unit.name}`;
      }
      return unit.name;
    }
    
    const parentPath = buildParentHierarchy(unit.parent, propertyName);
    return `${parentPath} -> ${unit.name}`;
  };

  // Get parent hierarchy string
  const getParentHierarchyString = (): string => {
    if (!unit) return '';
    
    if (!unit.parent) {
      return 'Root Unit'; // This is a root unit
    }
    
    const propertyName = unit.property?.name;
    return buildParentHierarchy(unit.parent, propertyName);
  };

  // Form setup


  // Update unit mutation
  const updateUnitMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Unit> }) => unitApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit', id] });
      setEditDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Failed to update unit:', error);
      
      let errorMessage = 'Failed to update unit. Please try again.';
      
      // Handle validation errors
      if (error.response?.status === 422) {
        if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.errors) {
          const errors = Object.values(error.response.data.errors).flat();
          errorMessage = errors.join('\n');
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } 
      // Handle authorization errors
      else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to update this unit.';
      }
      // Handle not found errors
      else if (error.response?.status === 404) {
        errorMessage = 'Unit not found. It may have been deleted.';
      }
      // Handle network errors
      else if (!error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    },
  });

  // Upload files mutation
  const uploadFilesMutation = useMutation({
    mutationFn: (files: FileList) => {
      if (unit) {
        return unitApi.uploadMedia(unit.id, Array.from(files), (progress) => {
          setUploadProgress(progress);
        });
      }
      return Promise.reject('No unit found');
    },
    onSuccess: () => {
      // Refetch unit data since backend automatically updates images
      queryClient.invalidateQueries({ queryKey: ['unit', id] });
      setUploadDialogOpen(false);
      setSelectedFiles(null);
      setUploadProgress(0);
    },
    onError: (error: any) => {
      console.error('Unit media upload error:', error);
      setUploadProgress(0);
      if (error.response?.status === 401) {
        alert('Session expired. Please refresh the page and try again.');
      } else {
        alert(error.response?.data?.message || 'Failed to upload media files. Please check your connection and try again.');
      }
    },
  });

  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: (imagePath: string) => {
      if (unit) {
        return unitApi.deleteMedia(unit.id, imagePath);
      }
      return Promise.reject('No unit found');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit', id] });
    },
  });

  // Update all daily rates mutation
  const updateAllRatesMutation = useMutation({
    mutationFn: ({ unitId, rate }: { unitId: number; rate: number }) => {
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;
      return dailyRatesApi.updateRates(unitId, {
        start_date: startDate,
        end_date: endDate,
        rate: rate
      });
    },
    onSuccess: () => {
      // Invalidate daily rates queries to refresh the calendar
      queryClient.invalidateQueries({ queryKey: ['dailyRates'] });
    },
    onError: (error: any) => {
      console.error('Failed to update daily rates:', error);
      alert('Failed to update daily rates. Please try again.');
    },
  });

  // Function to map old rental types to new ones
  const mapRentalTypes = (types: string[]): ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[] => {
    const typeMapping: { [key: string]: 'Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly' } = {
      'Hour': 'Hour',
      '3 Hours': '3 Hours',
      'Daily': 'Daily',
      'Monthly': 'Monthly',
      'Yearly': 'Yearly',
      'Daily/Monthly': 'Daily', // Map old combined type to Daily
      'Monthly/Yearly': 'Monthly', // Map old combined type to Monthly
    };
    
    return types
      .map(type => typeMapping[type])
      .filter((type): type is 'Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly' => type !== undefined);
  };

  const handleEditUnit = () => {
    if (unit) {
      setEditDialogOpen(true);
    }
  };

  const onSubmitEdit = async (data: UnitFormData) => {
    if (unit) {
      try {
        // First update the unit
        await updateUnitMutation.mutateAsync({ id: unit.id, data });
        
        // If "Update All Rates" is checked, update all daily rates for the year
        if (updateAllRates && data.rent_amount) {
          await updateAllRatesMutation.mutateAsync({ 
            unitId: unit.id, 
            rate: data.rent_amount 
          });
        }
        
        // Reset the checkbox
        setUpdateAllRates(false);
      } catch (error) {
        console.error('Failed to update unit or rates:', error);
      }
    }
  };

  const handleFileUpload = () => {
    if (selectedFiles && unit) {
      // Check file sizes before upload
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      const oversizedFiles = Array.from(selectedFiles).filter(file => file.size > maxSize);
      
      if (oversizedFiles.length > 0) {
        alert(`The following files are too large (max 100MB):\n${oversizedFiles.map(f => `• ${f.name} (${(f.size / 1024 / 1024).toFixed(1)}MB)`).join('\n')}`);
        return;
      }
      
      setUploadProgress(0);
      uploadFilesMutation.mutate(selectedFiles);
    }
  };

  const handleDeleteImage = (imagePath: string) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imagePath);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'vacant': return 'success';
      case 'occupied': return 'primary';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !unit) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load unit details. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {unit.name}
        </Typography>
        <Chip
          label={unit.status}
          color={getStatusColor(unit.status) as any}
          sx={{ textTransform: 'capitalize' }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Unit Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Unit Information</Typography>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleEditUnit}
                >
                  Edit Unit
                </Button>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Unit Number" 
                        secondary={unit.unit_number || 'N/A'} 
                      />
                    </ListItem>
                    {unit.parent && (
                      <ListItem>
                        <ListItemIcon><Star /></ListItemIcon>
                        <ListItemText 
                          primary="Parent Hierarchy" 
                          secondary={getParentHierarchyString()}
                          secondaryTypographyProps={{
                            sx: { 
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              color: 'primary.main'
                            }
                          }}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Type" 
                        secondary={unit.type} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Bed /></ListItemIcon>
                      <ListItemText 
                        primary="Bedrooms" 
                        secondary={unit.bedrooms || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Living Rooms" 
                        secondary={unit.living_room_count || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Men's Guest Rooms" 
                        secondary={unit.mens_guest_room_count || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Women's Guest Rooms" 
                        secondary={unit.womens_guest_room_count || 0} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Bathtub /></ListItemIcon>
                      <ListItemText 
                        primary="Bathrooms" 
                        secondary={unit.bathrooms || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><SquareFoot /></ListItemIcon>
                      <ListItemText 
                        primary="Square Feet" 
                        secondary={unit.square_feet || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AttachMoney /></ListItemIcon>
                      <ListItemText 
                        primary="Rent Amount" 
                        secondary={`$${(unit.rent_amount || 0).toLocaleString()}`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Bed /></ListItemIcon>
                      <ListItemText 
                        primary="Single Beds" 
                        secondary={unit.single_bed_count || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Bed /></ListItemIcon>
                      <ListItemText 
                        primary="Double Beds" 
                        secondary={unit.double_bed_count || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Max Persons" 
                        secondary={unit.max_persons || 1} 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {/* Additional Information Section */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Check-in Time" 
                        secondary={unit.checkin_time || '14:00'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary="Check-out Time" 
                        secondary={unit.checkout_time || '12:00'} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><AttachMoney /></ListItemIcon>
                      <ListItemText 
                        primary="Tax Rate" 
                        secondary={`${Number(unit.tax_rate || 0).toFixed(2)}%`} 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {unit.rental_types && unit.rental_types.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rental Types:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {mapRentalTypes(unit.rental_types).map((type, index) => (
                      <Chip key={index} label={type} variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              {unit.description && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Description:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {unit.description}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Media Gallery */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Media Gallery</Typography>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  Upload Media
                </Button>
              </Box>

              {/* Images Section */}
              {unit.images && unit.images.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoCamera fontSize="small" />
                    Images ({unit.images.length})
                  </Typography>
                  <ImageList cols={3} rowHeight={200}>
                    {unit.images.map((image, index) => (
                      <ImageListItem key={`image-${index}`}>
                        <img
                          src={getStorageUrl(image)}
                          alt={`Unit ${unit.name} - Image ${index + 1}`}
                          loading="lazy"
                          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                              onClick={() => handleDeleteImage(image)}
                            >
                              <Delete />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}

              {/* Videos Section */}
              {unit.videos && unit.videos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Videocam fontSize="small" />
                    Videos ({unit.videos.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {unit.videos.map((video, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`video-${index}`}>
                        <Paper 
                          sx={{ 
                            position: 'relative',
                            aspectRatio: '16/9',
                            overflow: 'hidden',
                            borderRadius: 1
                          }}
                        >
                          <video
                            controls
                            width="100%"
                            height="100%"
                            style={{ objectFit: 'cover' }}
                          >
                            <source src={getStorageUrl(video)} />
                            Your browser does not support the video tag.
                          </video>
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                              }
                            }}
                            onClick={() => handleDeleteImage(video)}
                          >
                            <Delete />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Empty State */}
              {(!unit.images || unit.images.length === 0) && (!unit.videos || unit.videos.length === 0) && (
                <Paper 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    bgcolor: 'grey.50',
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                    <PhotoCamera sx={{ fontSize: 48, color: 'grey.400' }} />
                    <Videocam sx={{ fontSize: 48, color: 'grey.400' }} />
                  </Box>
                  <Typography color="text.secondary">
                    No media files uploaded yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Upload images and videos to showcase this unit
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Property Facilities */}
          {unit.property?.facilities && unit.property.facilities.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('property_facilities')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {unit.property.facilities.map((facility: string, index: number) => (
                    <Chip
                      key={index}
                      label={facility}
                      icon={<CheckCircle />}
                      variant="outlined"
                      size="small"
                      color="primary"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Property Special Features */}
          {unit.property?.special_specs && unit.property.special_specs.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('special_features')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {unit.property.special_specs.map((feature: string, index: number) => (
                    <Chip
                      key={index}
                      label={feature}
                      icon={<Star />}
                      variant="outlined"
                      size="small"
                      color="secondary"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Edit Unit Dialog */}
      <EditUnitDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={onSubmitEdit}
        unit={unit}
        unitTypes={unitTypes}
        isLoading={updateUnitMutation.isPending || updateAllRatesMutation.isPending}
        updateAllRates={updateAllRates}
        onUpdateAllRatesChange={setUpdateAllRates}
      />

      {/* Upload Media Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Media Files</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can upload images (JPEG, PNG, GIF, SVG) and videos (MP4, MOV, AVI, WebM, MKV). Maximum file size: 100MB per file.
            </Typography>
            
            {uploadFilesMutation.isPending && uploadProgress > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">
                      {`${uploadProgress}%`}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {uploadProgress < 100 
                    ? `Uploading files... ${uploadProgress}%` 
                    : 'Processing files...'
                  }
                </Typography>
                <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                  Please don't close this page during upload to prevent session timeout.
                </Typography>
              </Box>
            )}
            <Paper
              sx={{
                p: 3,
                border: '2px dashed #ddd',
                borderRadius: 2,
                backgroundColor: '#fafafa',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#bbb'
                }
              }}
            >
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => setSelectedFiles(e.target.files)}
                style={{ 
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  position: 'absolute',
                  cursor: 'pointer'
                }}
              />
              <Box sx={{ position: 'relative', pointerEvents: 'none' }}>
                <CloudUpload sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Click to select files or drag and drop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Images and videos supported
                </Typography>
              </Box>
            </Paper>
            {selectedFiles && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="primary">
                  {selectedFiles.length} file(s) selected
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {Array.from(selectedFiles).map((file, index) => {
                    const sizeMB = file.size / 1024 / 1024;
                    const isOversized = sizeMB > 50;
                    return (
                      <Typography 
                        key={index} 
                        variant="caption" 
                        display="block" 
                        color={isOversized ? 'error' : 'text.secondary'}
                      >
                        • {file.name} ({sizeMB.toFixed(1)} MB) {isOversized && '⚠️ Too large'}
                      </Typography>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFiles || !unit || uploadFilesMutation.isPending}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UnitDetailsPage;
